import { useMemo } from "react";
import {
  CourtSvg,
  hexbin,
  ShotZoneTable,
  teamHexProps,
  useShotChartShots,
} from "../PlayerBlobPage/ShotChartSection";
import ShotMarksKey from "../ShotMarksKey";
import type { GameShotProfileSide, ReportSourceInfo } from "../types";
import type { ShotQueryContext } from "../ShotDetailModal/types";
import type { ShotProfileData } from "../../models/PlayerStats";
import { GAME_SHOT_SAMPLE_FLOOR } from "./utils";
import ShotQualityLabel from "./ShotQualityLabel";

// Chart scale (px per foot): the largest court that still leaves the zone
// table its columns beside it inside a quadrant on a letter page.
const COURT_K = 4.8;

/** A season (or any multi-game) report's block: the team's shots for / against, fetched like the coaches' season
 * chart, beside the blob's ranked zone table. */
export interface SeasonShots {
  teamNbaId: string;
  season: number;
  seasonType?: string;
  profile?: ShotProfileData | null;
  reportSource?: ReportSourceInfo;
  /** The quadrant's Overall row filters (halfcourt), as the coaches' HC page passes them */
  contextFilters?: ShotQueryContext["filters"];
}

/** The season hexbin: its own component so a game block (stored shots, no fetch) never needs the league API. */
function SeasonCourt({ season, perspective }: { season: SeasonShots; perspective: "offense" | "defense" }) {
  const { shots } = useShotChartShots({
    teamNbaId: season.teamNbaId,
    perspective,
    season: season.season,
    seasonType: season.seasonType,
    reportSource: season.reportSource,
    contextFilters: season.contextFilters,
  });
  const bins = useMemo(() => (shots ? hexbin(shots.filter((s) => s.x != null)) : []), [shots]);
  return (
    <>
      <CourtSvg bins={bins} k={COURT_K} {...teamHexProps(shots?.length ?? 0)} />
      {/* data-hex-loaded: the print harness waits on it (the page's ready flag covers the blob, not this fetch) */}
      <div className="fn" data-hex-loaded={shots ? "true" : undefined}>
        {shots ? `Hex size = volume; color = TS% · ${shots.length.toLocaleString()} shots` : "loading…"}
      </div>
    </>
  );
}

/**
 * One side's halfcourt shots beside this year's zone breakdown. A game: make / miss chart (hexbins are unreadable at
 * one game's volume), zones scored vs the rolling game population. A season (`season`): the coaches' season hexbin
 * (the same fetch and team settings as ShotChartSection), zones ranked vs the league. Ours on offense, theirs on
 * defense.
 */
export default function GameShotBlock({
  profile,
  season,
  perspective,
  extra,
}: {
  profile?: GameShotProfileSide | null;
  season?: SeasonShots;
  /** Ours on offense, theirs on defense: which way a zone's share reads as good */
  perspective: "offense" | "defense";
  /** Under the zone table, beside the court (the zone-defense row): the court is the taller column, so it adds no
   * height to the page */
  extra?: React.ReactNode;
}) {
  const zones = profile?.zones ?? season?.profile?.zones ?? [];
  const hasX = zones.some((z) => z.xts != null);
  return (
    <div className="gsb">
      <div className="court">
        {profile ? (
          <>
            <CourtSvg dots={profile.shots} names={profile.names} k={COURT_K} />
            <div className="fn">
              <ShotMarksKey shots={profile.shots} />
            </div>
          </>
        ) : (
          season && <SeasonCourt season={season} perspective={perspective} />
        )}
      </div>
      <div className="tbls">
        <ShotZoneTable
          zones={zones}
          hasX={hasX}
          columns={["share", "tsa", "xts", "ts"]}
          labels={{ xts: ShotQualityLabel }}
          lowFloor={profile ? GAME_SHOT_SAMPLE_FLOOR : undefined}
          ratePerspective={perspective}
        />
        <div className="fn">
          Sub-zone rates are % of the parent zone.
          {profile && ` Grey rows: fewer than ${GAME_SHOT_SAMPLE_FLOOR} shots.`}
        </div>
        {extra}
      </div>
    </div>
  );
}
